import { useQuery } from "@tanstack/react-query";
import { memo } from "react";

import { Button } from "@/components/base/button";
import { Toolbar } from "@/components/base/toolbar";
import type { Tournament, TournamentDivision } from "@/db/schema";
import { usePanelModals } from "@/hooks/panel-modals";
import { useTRPC } from "@/integrations/trpc/react";

import { useCanUpdateTournament, useTournamentDivision } from "../../context";
import { CompleteTournamentDivisionForm } from "../../controls/complete-tournament-division";
import { CreatePlayoffsForm } from "../../controls/create-playoffs";
import { Bracket } from "./bracket";

export const PlayoffsPanel = memo(function PlayoffsPanel({
  id,
  tournamentDivisionId,
  isActive,
}: Pick<Tournament, "id"> & {
  tournamentDivisionId: TournamentDivision["id"];
  isActive: boolean;
}) {
  const trpc = useTRPC();

  const canEdit = useCanUpdateTournament();

  const division = useTournamentDivision();

  const { data } = useQuery({
    ...trpc.tournaments.getPlayoffs.queryOptions({ tournamentDivisionId }),
    enabled: isActive,
  });

  const { setActiveModal, modalProps } = usePanelModals<
    "createPlayoffs" | "completeDivision"
  >();

  const hasPlayoffs = data && data.length > 0;
  const playoffsComplete = hasPlayoffs && data.every((m) => m.winnerId != null);

  return (
    <>
      {canEdit && division && (
        <div className="mx-auto max-w-4xl px-3 pt-6 pb-4">
          <Toolbar
            aria-label="Playoff controls"
            className="ml-auto flex w-fit items-center gap-2"
          >
            <Button
              variant={hasPlayoffs ? "outline" : "solid"}
              color={hasPlayoffs ? undefined : "primary"}
              onPress={() => setActiveModal("createPlayoffs")}
            >
              {hasPlayoffs ? "Recreate Playoffs" : "Create Playoffs"}
            </Button>
            <Button
              variant={
                playoffsComplete && division?.status !== "complete"
                  ? "solid"
                  : "outline"
              }
              color={
                playoffsComplete && division?.status !== "complete"
                  ? "primary"
                  : undefined
              }
              isDisabled={!playoffsComplete}
              onPress={() => setActiveModal("completeDivision")}
            >
              {division?.status === "complete"
                ? "Reverify Division"
                : "Verify Division"}
            </Button>
          </Toolbar>
          <CreatePlayoffsForm
            tournamentId={id}
            division={division}
            {...modalProps("createPlayoffs")}
          />
          <CompleteTournamentDivisionForm
            tournamentId={id}
            division={division}
            {...modalProps("completeDivision")}
          />
        </div>
      )}
      {hasPlayoffs ? (
        <Bracket matches={data} />
      ) : (
        <div className="mx-auto max-w-4xl px-3 py-12">
          <p className="py-8 text-center text-gray-500">
            No playoffs have been created yet.
          </p>
        </div>
      )}
    </>
  );
});
